import { auth, db } from '../firebase';
import logger from '../utils/logger';
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver
} from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

/**
 * Service for handling Two-Factor Authentication (2FA) operations
 * Uses Firebase Auth Multi-Factor Authentication with phone verification
 */
class TwoFactorService {
  constructor() {
    this.recaptchaVerifier = null;
    this.verificationId = null;
  }

  /**
   * Initialize reCAPTCHA verifier for phone authentication
   * @param {string} containerId - ID of the HTML element to render reCAPTCHA
   * @returns {RecaptchaVerifier}
   */
  initRecaptcha(containerId) {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }

    this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved
      },
      'expired-callback': () => {
        // reCAPTCHA expired
        logger.log('reCAPTCHA expired');
      }
    });

    return this.recaptchaVerifier;
  }

  /**
   * Clear reCAPTCHA verifier
   */
  clearRecaptcha() {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
  }

  /**
   * Check if the current user has 2FA enabled
   * @returns {boolean}
   */
  isEnabled() {
    const user = auth.currentUser;
    if (!user) return false;

    const multiFactorUser = multiFactor(user);
    return multiFactorUser.enrolledFactors.length > 0;
  }

  /**
   * Get enrolled 2FA factors for current user
   * @returns {Array} List of enrolled factors
   */
  getEnrolledFactors() {
    const user = auth.currentUser;
    if (!user) return [];

    const multiFactorUser = multiFactor(user);
    return multiFactorUser.enrolledFactors.map(factor => ({
      uid: factor.uid,
      displayName: factor.displayName || 'Phone',
      factorId: factor.factorId,
      enrollmentTime: factor.enrollmentTime
    }));
  }

  /**
   * Start 2FA enrollment - send verification code to phone
   * @param {string} phoneNumber - Phone number in E.164 format (e.g., +1234567890)
   * @param {string} recaptchaContainerId - ID of reCAPTCHA container element
   * @returns {Promise<string>} Verification ID
   */
  async startEnrollment(phoneNumber, recaptchaContainerId) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be signed in to enable 2FA');
    }

    // Initialize reCAPTCHA
    this.initRecaptcha(recaptchaContainerId);

    try {
      const multiFactorUser = multiFactor(user);
      const session = await multiFactorUser.getSession();

      const phoneInfoOptions = {
        phoneNumber: phoneNumber,
        session: session
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      this.verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        this.recaptchaVerifier
      );

      return this.verificationId;
    } catch (error) {
      this.clearRecaptcha();
      throw this.handleError(error);
    }
  }

  /**
   * Complete 2FA enrollment - verify code and enroll
   * @param {string} verificationCode - 6-digit code from SMS
   * @param {string} displayName - Optional friendly name for this 2FA method
   * @returns {Promise<void>}
   */
  async completeEnrollment(verificationCode, displayName = 'Phone') {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be signed in to enable 2FA');
    }

    if (!this.verificationId) {
      throw new Error('No pending verification. Please request a new code.');
    }

    try {
      const phoneAuthCredential = PhoneAuthProvider.credential(
        this.verificationId,
        verificationCode
      );

      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
      const multiFactorUser = multiFactor(user);

      await multiFactorUser.enroll(multiFactorAssertion, displayName);

      // Update user document to track 2FA status
      await this.update2FAStatus(user.uid, true);

      this.verificationId = null;
      this.clearRecaptcha();
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Unenroll a 2FA factor
   * @param {string} factorUid - UID of the factor to remove
   * @returns {Promise<void>}
   */
  async unenroll(factorUid) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be signed in to disable 2FA');
    }

    try {
      const multiFactorUser = multiFactor(user);
      const factorToRemove = multiFactorUser.enrolledFactors.find(
        factor => factor.uid === factorUid
      );

      if (!factorToRemove) {
        throw new Error('Factor not found');
      }

      await multiFactorUser.unenroll(factorToRemove);

      // Update user document if no more factors enrolled
      if (multiFactorUser.enrolledFactors.length === 0) {
        await this.update2FAStatus(user.uid, false);
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle MFA challenge during sign-in
   * Returns resolver that can be used to complete sign-in
   * @param {Error} error - The MFA required error from sign-in
   * @returns {Object} MFA resolver and hints
   */
  getMFAResolver(error) {
    if (error.code !== 'auth/multi-factor-auth-required') {
      throw error;
    }

    const resolver = getMultiFactorResolver(auth, error);
    const hints = resolver.hints.map(hint => ({
      uid: hint.uid,
      displayName: hint.displayName || 'Phone',
      phoneNumber: hint.phoneNumber ? this.maskPhoneNumber(hint.phoneNumber) : null,
      factorId: hint.factorId
    }));

    return { resolver, hints };
  }

  /**
   * Send verification code for MFA sign-in
   * @param {Object} resolver - MFA resolver from getMFAResolver
   * @param {number} hintIndex - Index of the hint to use (default: 0)
   * @param {string} recaptchaContainerId - ID of reCAPTCHA container
   * @returns {Promise<string>} Verification ID
   */
  async sendMFACode(resolver, hintIndex = 0, recaptchaContainerId) {
    this.initRecaptcha(recaptchaContainerId);

    try {
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[hintIndex],
        session: resolver.session
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      this.verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        this.recaptchaVerifier
      );

      return this.verificationId;
    } catch (error) {
      this.clearRecaptcha();
      throw this.handleError(error);
    }
  }

  /**
   * Complete MFA sign-in with verification code
   * @param {Object} resolver - MFA resolver
   * @param {string} verificationCode - 6-digit code from SMS
   * @returns {Promise<UserCredential>}
   */
  async completeMFASignIn(resolver, verificationCode) {
    if (!this.verificationId) {
      throw new Error('No pending verification. Please request a new code.');
    }

    try {
      const phoneAuthCredential = PhoneAuthProvider.credential(
        this.verificationId,
        verificationCode
      );

      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion);

      this.verificationId = null;
      this.clearRecaptcha();

      return userCredential;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update 2FA status in Firestore user document
   * @private
   */
  async update2FAStatus(userId, enabled) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        twoFactorEnabled: enabled,
        twoFactorUpdatedAt: new Date()
      });
    } catch (error) {
      logger.error('Error updating 2FA status:', error);
      // Don't throw - this is supplementary tracking
    }
  }

  /**
   * Get 2FA status from Firestore
   * @param {string} userId - User ID
   * @returns {Promise<Object>} 2FA status
   */
  async get2FAStatus(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          enabled: data.twoFactorEnabled || false,
          updatedAt: data.twoFactorUpdatedAt || null
        };
      }

      return { enabled: false, updatedAt: null };
    } catch (error) {
      logger.error('Error getting 2FA status:', error);
      return { enabled: false, updatedAt: null };
    }
  }

  /**
   * Mask phone number for display
   * @private
   */
  maskPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    // Show last 4 digits: +1******1234
    const last4 = phoneNumber.slice(-4);
    const masked = phoneNumber.slice(0, 2) + '******' + last4;
    return masked;
  }

  /**
   * Format phone number to E.164 format
   * @param {string} phoneNumber - Raw phone number
   * @param {string} countryCode - Country code (default: +1 for US)
   * @returns {string} Formatted phone number
   */
  formatPhoneNumber(phoneNumber, countryCode = '+1') {
    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');

    // If already has country code, use as-is
    if (phoneNumber.startsWith('+')) {
      return '+' + digits;
    }

    // Add country code
    return countryCode + digits;
  }

  /**
   * Handle Firebase Auth errors
   * @private
   */
  handleError(error) {
    const errorMessages = {
      'auth/invalid-phone-number': 'Invalid phone number format. Please use format: +1234567890',
      'auth/missing-phone-number': 'Please enter a phone number',
      'auth/quota-exceeded': 'Too many requests. Please try again later.',
      'auth/user-disabled': 'This account has been disabled',
      'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
      'auth/code-expired': 'Verification code has expired. Please request a new one.',
      'auth/session-expired': 'Session expired. Please start over.',
      'auth/too-many-requests': 'Too many attempts. Please try again later.',
      'auth/requires-recent-login': 'Please sign out and sign in again to enable 2FA.',
      'auth/second-factor-already-in-use': 'This phone number is already registered for 2FA.',
      'auth/maximum-second-factor-count-exceeded': 'Maximum number of 2FA methods reached.',
      'auth/unsupported-first-factor': '2FA is not supported with your current sign-in method.',
      'auth/unverified-email': 'Please verify your email before enabling 2FA.'
    };

    const message = errorMessages[error.code] || error.message || 'An error occurred';
    const enhancedError = new Error(message);
    enhancedError.code = error.code;
    enhancedError.originalError = error;

    return enhancedError;
  }
}

export const twoFactorService = new TwoFactorService();
